/**
 * Email Sequence Templates Index
 * Pre-built sequence templates based on best practices from
 * HubSpot, Intercom, Mailchimp, Duolingo, Netflix, Spotify, Slack, Dropbox, and other billion-dollar companies
 *
 * 33+ templates covering:
 * - Onboarding & Activation
 * - Retention & Win-back
 * - Engagement & Re-engagement
 * - Milestones & Celebrations
 * - Revenue & Upsell
 * - Growth & Referral
 * - Education & Nurture
 * - Lifecycle & AI Copilot-specific
 */

// Import all template collections
import { ONBOARDING_TEMPLATES } from "./onboarding";
import { RETENTION_TEMPLATES } from "./retention";
import { ENGAGEMENT_TEMPLATES } from "./engagement";
import { MILESTONE_TEMPLATES } from "./milestone";
import { REVENUE_TEMPLATES } from "./revenue";
import { GROWTH_TEMPLATES } from "./growth";
import { EDUCATION_TEMPLATES } from "./education";
import { LIFECYCLE_TEMPLATES } from "./lifecycle";

// Re-export types
export * from "./types";

// Re-export individual collections
export { ONBOARDING_TEMPLATES } from "./onboarding";
export { RETENTION_TEMPLATES } from "./retention";
export { ENGAGEMENT_TEMPLATES } from "./engagement";
export { MILESTONE_TEMPLATES } from "./milestone";
export { REVENUE_TEMPLATES } from "./revenue";
export { GROWTH_TEMPLATES } from "./growth";
export { EDUCATION_TEMPLATES } from "./education";
export { LIFECYCLE_TEMPLATES } from "./lifecycle";

// Combined templates array
export const ALL_SEQUENCE_TEMPLATES = [
  ...ONBOARDING_TEMPLATES,
  ...RETENTION_TEMPLATES,
  ...ENGAGEMENT_TEMPLATES,
  ...MILESTONE_TEMPLATES,
  ...REVENUE_TEMPLATES,
  ...GROWTH_TEMPLATES,
  ...EDUCATION_TEMPLATES,
  ...LIFECYCLE_TEMPLATES,
];

// Template categories for the gallery
export const SEQUENCE_TEMPLATE_CATEGORIES = [
  {
    id: "onboarding",
    name: "Onboarding",
    description: "Guide new users from signup to success",
    icon: "RocketOutlined",
    color: "#0ea5e9",
    templates: ONBOARDING_TEMPLATES,
  },
  {
    id: "retention",
    name: "Retention",
    description: "Convert trials and recover churned users",
    icon: "HeartOutlined",
    color: "#ef4444",
    templates: RETENTION_TEMPLATES,
  },
  {
    id: "engagement",
    name: "Engagement",
    description: "Re-engage inactive users and drive feature adoption",
    icon: "ThunderboltOutlined",
    color: "#10b981",
    templates: ENGAGEMENT_TEMPLATES,
  },
  {
    id: "milestone",
    name: "Milestones",
    description: "Celebrate achievements and usage milestones",
    icon: "TrophyOutlined",
    color: "#f59e0b",
    templates: MILESTONE_TEMPLATES,
  },
  {
    id: "revenue",
    name: "Revenue",
    description: "Upgrade nudges, upsells, and expansion revenue",
    icon: "DollarOutlined",
    color: "#8b5cf6",
    templates: REVENUE_TEMPLATES,
  },
  {
    id: "growth",
    name: "Growth",
    description: "Referrals, reviews, and viral loops",
    icon: "RiseOutlined",
    color: "#ec4899",
    templates: GROWTH_TEMPLATES,
  },
  {
    id: "education",
    name: "Education",
    description: "Tips, courses, and thought leadership",
    icon: "BulbOutlined",
    color: "#f97316",
    templates: EDUCATION_TEMPLATES,
  },
  {
    id: "lifecycle",
    name: "Lifecycle",
    description: "AI Copilot activation and engagement sequences",
    icon: "SyncOutlined",
    color: "#06b6d4",
    templates: LIFECYCLE_TEMPLATES,
  },
];

// Helper function to get template by ID
export const getSequenceTemplateById = (id: string) => {
  return ALL_SEQUENCE_TEMPLATES.find((template) => template.id === id);
};

// Helper function to get templates by type
export const getSequenceTemplatesByType = (type: string) => {
  return ALL_SEQUENCE_TEMPLATES.filter((template) => template.type === type);
};

// Helper function to get templates by tag
export const getSequenceTemplatesByTag = (tag: string) => {
  return ALL_SEQUENCE_TEMPLATES.filter((template) => template.tags.includes(tag));
};

// Helper function to get templates by difficulty
export const getSequenceTemplatesByDifficulty = (difficulty: "beginner" | "intermediate" | "advanced") => {
  return ALL_SEQUENCE_TEMPLATES.filter((template) => template.difficulty === difficulty);
};
